import React, { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useFlash from "go-alert/AlertMessage";
import { RenderImage } from "go-app/components/ImageRenderer/ListImageName";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatDate from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import { ReactComponent as RemoveSVG } from "go-core/images/svg/actions/remove.svg";
import RenderPaymentMethod from "../../../../../../../../../components/Common/Renderers/RenderPaymentMethod";
import { TransactionApi } from "../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../services/Api/api";
import InvoiceCard from "../../Orders/components/InvoiceCard";
import { getPaymentMethodExternalProvider } from "../utils/utils";
import TransactionDetailsCard from "./TransactionDetailsCard";
import TransactionInfosCard from "./TransactionInfosCard";
import TransactionInvoiceCard from "./TransactionInvoiceCard";
import TransactionOrderCard from "./TransactionOrderCard";
import TransactionPreviewActions from "./TransactionPreviewActions";
import TransactionReportPaidCard from "./TransactionReportPaidCard";
import { transactionPreviewParams } from "./utils";

interface Props {
	id: number | null;
	onClose: () => void;
	styles?: Record<string, any>;
	_transaction?: TransactionApi;
	handleSuccessAction?: () => void;
	canChangePaymentMethod?: boolean;
}

const TransactionPreview: FC<Props> = ({
	id,
	onClose,
	styles,
	_transaction,
	canChangePaymentMethod,
	handleSuccessAction,
}) => {
	const { t } = useTranslation();
	const [transaction, setTransaction] = useState<TransactionApi | undefined>(_transaction);
	const [loading, setLoading] = useState(false);
	const [applyMarginToTransactionPreview, setApplyMarginToTransactionPreview] = useState(() =>
		Boolean(document.getElementsByName("intercom-banner-frame")[0])
	);
	const { addFlash } = useFlash();
	const ref = useRef<HTMLDivElement>(null);
	const [preventClosingPreview, setPreventClosingPreview] = useState(false);
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const { infos, source } = transaction ?? {};
	const infosToDisplay = infos?.filter((info) => info.type !== "VENDOR");
	const displayInfosCard = infosToDisplay && infosToDisplay.length > 0;
	const providerName = getPaymentMethodExternalProvider(t, infos);
	const displayDetailsCard = Object.keys(source ?? {}).length > 0;

	useEffect(() => {
		return () => {
			handleChangeTabTitle(t("modules.transaction.header.title"));
		};
	}, []);

	useEffect(() => {
		const mutationObserver = new MutationObserver(() => {
			setTimeout(() => {
				const banner = document.getElementsByName("intercom-banner-frame")[0];

				if (banner && !applyMarginToTransactionPreview) setApplyMarginToTransactionPreview(true);
			}, 300);
		});
		mutationObserver.observe(document.getElementsByTagName("body")[0], {
			attributes: false,
			childList: true,
			subtree: false,
		});

		return () => mutationObserver.disconnect();
	}, [applyMarginToTransactionPreview]);

	useEffect(() => {
		fetchTransaction();
	}, [id]);

	const handleHideDropdown = (event: KeyboardEvent) => {
		if (preventClosingPreview) return;
		if (event.key === "Escape") {
			onClose();
		}
	};

	const handleClickOutside = (event: MouseEvent) => {
		if (preventClosingPreview) return;
		if (ref.current && !ref.current?.contains(event.target as Node)) {
			onClose();
		}
	};

	useEffect(() => {
		document.addEventListener("keydown", handleHideDropdown, true);
		document.addEventListener("click", handleClickOutside, true);
		return () => {
			document.removeEventListener("keydown", handleHideDropdown, true);
			document.removeEventListener("click", handleClickOutside, true);
		};
	});

	const fetchTransaction = async () => {
		try {
			if (!transaction && id) {
				setLoading(true);
				const res = await api.organization().getTransaction(id, transactionPreviewParams);
				setTransaction(res);
				handleChangeTabTitle(`#${res.id} | ${t("modules.transaction.header.title")}`);
				setLoading(false);
			} else {
				setTransaction(transaction);
				handleChangeTabTitle(`#${transaction?.id || ""} | ${t("modules.transaction.header.title")}`);
			}
		} catch (err) {
			setLoading(false);
			onClose();
			handleError.alert(err, addFlash);
		}
	};

	const style = styles ? { ...styles } : undefined;

	if (loading || !transaction) {
		return (
			<div className={`list-preview`} style={{ ...style, zIndex: 9999 }}>
				<div className={"list-preview-content"}>
					<LoadingContainer />
				</div>
			</div>
		);
	}

	return (
		<div
			className={`list-preview transaction-preview`}
			ref={ref}
			style={{ ...style, top: applyMarginToTransactionPreview ? "48px" : "0" }}
		>
			<div className="list-preview-header">
				<RenderImage data={{}} date={transaction.created_at} />
				<div className="header-content">
					<div className="title">#{transaction.id}</div>
					<div className="info">
						<span className={"me-4"}>{FormatDate(transaction.created_at)}</span>
						{transaction.amount && <span>{FormatMoney(transaction.amount)}</span>}
					</div>
					{transaction.payment_method && (
						<div className="info">
							<RenderPaymentMethod
								name={transaction.payment_method.name}
								status={transaction.status}
								type={transaction.payment_method.receipt_type}
								providerName={providerName}
							/>
						</div>
					)}
				</div>
				<div className={"list-preview-close"}>
					<RemoveSVG className={"icon"} onClick={onClose} />
				</div>
			</div>
			<div className="list-preview-content content-preview-body">
				{transaction.contexts
					.filter((ctx) => ctx.id)
					.sort((a, b) => {
						if (a.type === "ORDER") return -1;
						if (a.type === "INVOICE" && b.type === "CORRECTION") return -1;
						if (a.type === "CORRECTION" && b.type === "INVOICE") return 1;
						return 0;
					})
					.map((ctx) => {
						if (ctx.type === "REPORT_PAID") {
							return (
								<TransactionReportPaidCard
									key={ctx.id}
									contextId={ctx.number}
									id={ctx.id}
									inconsistent_reference_id={transaction.inconsistent_reference_id}
								/>
							);
						}
						if (ctx.type === "ORDER") {
							return (
								<React.Fragment key={ctx.id}>
									<TransactionOrderCard
										orderId={ctx.number}
										id={ctx.id}
										inconsistentReferenceId={transaction.inconsistent_reference_id}
									/>
									{displayDetailsCard && (
										<TransactionDetailsCard
											inconsistentReferenceId={transaction.inconsistent_reference_id}
											source={transaction?.source}
										/>
									)}
									{displayInfosCard && (
										<TransactionInfosCard
											infos={infosToDisplay}
											inconsistentReferenceId={transaction.inconsistent_reference_id}
											currency={transaction.amount.currency}
										/>
									)}
								</React.Fragment>
							);
						}
						if (transaction?.contexts.filter((context) => context.type === "ORDER").length === 0)
							return (
								<TransactionInvoiceCard
									key={ctx.id}
									invoiceId={ctx.number}
									id={ctx.id}
									inconsistent_reference_id={transaction.inconsistent_reference_id}
								/>
							);
						return (
							<InvoiceCard
								key={ctx.id}
								invoice={ctx}
								inconsistent_reference_id={transaction.inconsistent_reference_id}
							/>
						);
					})}
				<TransactionPreviewActions
					transaction={transaction}
					setTransaction={setTransaction}
					handleSuccessAction={handleSuccessAction}
					canChangePaymentMethod={canChangePaymentMethod}
					setPreventClosingPreview={setPreventClosingPreview}
				/>
			</div>
		</div>
	);
};

export default TransactionPreview;
